import { clearPersonalStorage } from '../lib/storage';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInAnonymously, signOut as fbSignOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { LEGAL_VERSIONS } from '../config/site';

const AuthContext = createContext(null);
const refreshTried = new Set();

export const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Turns the raw licence record into the handful of states the interface needs.
export function deriveLicense(raw) {
  if (!raw || !raw.type) return { status: 'none', hasAccess: false };
  const now = Date.now();
  if (raw.type === 'trial') {
    const endsAt = toDate(raw.expiration_date);
    const active = raw.is_active !== false && endsAt && endsAt.getTime() > now;
    return {
      status: active ? 'trial' : 'expired', plan: 'trial', endsAt, hasAccess: !!active,
      daysLeft: endsAt ? Math.max(0, Math.ceil((endsAt.getTime() - now) / 86400000)) : 0,
    };
  }
  if (raw.type === 'individual') {
    const endsAt = toDate(raw.current_period_end);
    const inPeriod = !endsAt || endsAt.getTime() > now;
    const active = raw.is_active !== false && inPeriod;
    let status = active ? 'active' : 'expired';
    if (active && raw.cancel_at_period_end) status = 'cancelling';
    if (raw.subscription_status === 'past_due') status = 'past_due';
    return {
      status, plan: 'individual', billing: raw.billing_period || 'monthly', endsAt, hasAccess: !!active,
      daysLeft: endsAt ? Math.max(0, Math.ceil((endsAt.getTime() - now) / 86400000)) : null,
      cancelAtPeriodEnd: !!raw.cancel_at_period_end,
      hasStripe: !!raw.stripe_customer_id,
    };
  }
  if (raw.type === 'pending') return { status: 'pending', hasAccess: false };
  return { status: raw.is_active ? 'active' : 'expired', hasAccess: !!raw.is_active };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = still checking
  const [profile, setProfile] = useState(null);
  const [profileReady, setProfileReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  // Team access (Admin → Messages) comes from a custom claim on the sign-in token.
  useEffect(() => {
    let live = true;
    if (!user || user.isAnonymous) { setIsAdmin(false); return undefined; }
    user.getIdTokenResult().then((r) => { if (live) setIsAdmin(r.claims?.admin === true); }).catch(() => {});
    return () => { live = false; };
  }, [user]);

  useEffect(() => {
    if (!user || user.isAnonymous) {
      setProfile(null);
      setProfileReady(true);
      return undefined;
    }
    setProfileReady(false);
    // Firestore is only needed once someone has signed in, so it is fetched then — guests and
    // first-time visitors never download it on marketing pages.
    let unsub = () => {};
    let cancelled = false;
    Promise.all([import('firebase/firestore'), import('../lib/db')]).then(([{ doc, onSnapshot }, { db }]) => {
      if (cancelled) return;
      unsub = onSnapshot(
        doc(db, 'users', user.uid),
        (snap) => { setProfile(snap.exists() ? snap.data() : null); setProfileReady(true); },
        () => { setProfile(null); setProfileReady(true); },
      );
    }).catch(() => { if (!cancelled) { setProfile(null); setProfileReady(true); } });
    return () => { cancelled = true; unsub(); };
  }, [user]);

  // Existing subscribers: if the licence record looks expired but is linked to Stripe, ask the
  // server to re-check Stripe once per visit (fixes records left stale by the old payment setup).
  const lic = profile?.license;
  useEffect(() => {
    if (!user || user.isAnonymous || !lic || refreshTried.has(user.uid)) return;
    const d = deriveLicense(lic);
    const linked = lic.stripe_subscription_id || lic.stripe_customer_id || lic.type === 'pending';
    if (!linked || d.hasAccess) return;
    refreshTried.add(user.uid);
    import('../lib/api').then(({ callFunction }) => callFunction('refresh_license')).catch(() => {});
  }, [user, lic]);

  // Guests get a temporary anonymous session only when they start an analysis —
  // never just for visiting a page. These sessions are deleted after 30 days.
  const ensureSession = useCallback(async () => {
    if (auth.currentUser) return auth.currentUser;
    const cred = await signInAnonymously(auth);
    return cred.user;
  }, []);

  const getToken = useCallback(async () => {
    const u = await ensureSession();
    return u.getIdToken();
  }, [ensureSession]);

  const signOut = useCallback(async () => {
    clearPersonalStorage();
    await fbSignOut(auth);
  }, []);

  const value = useMemo(() => {
    const loading = user === undefined || (user && !user.isAnonymous && !profileReady);
    const isMember = !!user && !user.isAnonymous;
    const license = deriveLicense(profile?.license);
    const needsOnboarding = isMember && profileReady && (!profile?.age_verified || profile?.terms_version !== LEGAL_VERSIONS.terms);
    return {
      user: user || null,
      profile,
      loading,
      isMember,
      isGuest: !isMember,
      isAdmin,
      license,
      needsOnboarding,
      emailVerified: !!(user?.emailVerified || profile?.email_verified),
      ensureSession,
      getToken,
      signOut,
    };
  }, [user, profile, profileReady, isAdmin, ensureSession, getToken, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
